<template>
    <div class="event-screen auth event-entry w-100 h-100">
        <div class="custom-background" :style="{ backgroundImage: `url('${$store.state.game.images?.auth_image}')`}"></div>
        <button type="button" class="btn btn-sm btn-outline-light position-absolute top-0 end-0 m-3 auth-logout" title="Cerrar sesión" @click="logout"><i class="bi bi-box-arrow-left"></i></button>
        <div class="event-entry-layout">
            <div v-if="needs_code_gate" class="auth-picker d-flex flex-column justify-content-center align-items-center animate__animated animate__fadeIn">
                <JoinByCode :initial_code="$route.params.event_code" :auto_submit="!!$route.params.event_code" />
            </div>

            <div v-else-if="avatar_menu == false" class="auth-picker d-flex flex-column animate__animated animate__fadeIn">
                <div class="auth-picker-header">
                    <div class="auth-picker-step">Paso 1 de 2</div>
                    <h1 class="username m-0">Identifícate</h1>
                </div>

                <div class="auth-picker-body d-flex flex-column justify-content-center align-items-center flex-fill">
                    <h2 class="auth-picker-title">Introduce el nombre de tu equipo</h2>
                    <input ref="teamName" aria-label="Nombre de tu equipo" autocomplete="nickname" type="text" placeholder="Escribe aquí el nombre de usuario..." class="form-control auth-picker-input" :disabled="checking_username" @keyup="handle_username($event)" v-model="username">
                    <small class="text-danger mt-2">{{ username_errors }}</small>
                </div>

                <div class="auth-picker-footer d-flex">
                    <button type="button" class="btn btn-warning flex-fill" :disabled="!username || checking_username" @click="toggle_avatar_menu">{{ checking_username ? 'Comprobando...' : 'Continuar' }}</button>
                </div>
            </div>

            <div v-else class="auth-picker d-flex flex-column animate__animated animate__fadeIn">
                <div class="auth-picker-header">
                    <div class="auth-picker-step">Paso 2 de 2</div>
                    <h1 class="username m-0">{{ username }}</h1>
                </div>

                <div class="auth-picker-body d-flex flex-column justify-content-center align-items-center flex-fill overflow-auto">
                    <h2 class="auth-picker-title">Elige tu maillot</h2>
                    <Avatar3DPicker :key="jersey_model_url" :model-url="jersey_model_url" :color-options="jersey_color_options" :taken-colors="taken_jersey_colors" v-model="jersey_color" />
                    <!-- Collapsed by default (unobtrusive) but a real,
                         clickable attribution when opened - CC-BY requires
                         the credit + license link to be reasonably
                         discoverable where the work is used, not that it be
                         prominent. A plain title="" tooltip wouldn't give a
                         clickable license link, so this uses a native
                         <details> disclosure instead. -->
                    <details class="jersey-credits mt-2">
                        <summary>Créditos del modelo 3D</summary>
                        <span>"T-shirt" por <a href="https://poly.pizza/m/bdOMzzh-fSl" target="_blank" rel="noopener">Poly by Google</a>, licencia <a href="https://creativecommons.org/licenses/by/3.0/" target="_blank" rel="noopener">CC-BY 3.0</a>.</span>
                    </details>

                    <TeamMarquee :teams="registered_teams" class="mt-3" />
                </div>

                <div class="auth-picker-footer d-flex">
                    <button class="btn btn-outline-secondary flex-fill" :disabled="submitting_auth" @click="return_username">Volver</button>
                    <button class="btn btn-warning flex-fill ms-3" :disabled="submitting_auth" @click="client_auth">{{ submitting_auth ? 'Enviando...' : 'Siguiente' }}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Avatar3DPicker from '@/components/Avatar3DPicker.vue'
import TeamMarquee from '@/components/TeamMarquee.vue'
import JoinByCode from '@/components/JoinByCode.vue'
import { get_active_jersey_model_url } from '@/utils/jersey_model'

export default {
    name: 'Auth',
    components: {
        Avatar3DPicker,
        TeamMarquee,
        JoinByCode,
    },
    data(){
        return {
            utils: require('../../../shared/utils'),
            username: null,
            username_errors: null,
            checking_username: false,
            submitting_auth: false,
            avatar_menu: false,
            // Fixed palette (not an open color picker) is deliberate - it's
            // what makes "this color is already taken" a meaningful,
            // detectable state instead of every player picking an
            // effectively-unique arbitrary hex.
            jersey_color_options: [
                { label: 'Rojo', hex: '#c0392b' },
                { label: 'Azul', hex: '#2980b9' },
                { label: 'Verde', hex: '#27ae60' },
                { label: 'Amarillo', hex: '#f1c40f' },
                { label: 'Naranja', hex: '#e67e22' },
                { label: 'Morado', hex: '#8e44ad' },
                { label: 'Negro', hex: '#17202b' },
                { label: 'Blanco', hex: '#f4f4f4' },
                { label: 'Rosa', hex: '#e84393' },
                { label: 'Turquesa', hex: '#16a085' },
                { label: 'Granate', hex: '#7b241c' },
                { label: 'Marino', hex: '#1b2631' },
            ],
            jersey_color: '#c0392b',
        }
    },
    computed: {
        // Guests must supply a valid whole-event or per-stage code (see
        // JoinByCode.vue / VALIDATE_JOIN_CODE) before this screen's username/
        // avatar wizard shows - true until that check succeeds.
        needs_code_gate(){
            return !this.$store.state.join_verified;
        },
        // game.clients already carries avatar_style for every connected
        // player (see server.js's list_clients()) - a GUEST on this very
        // screen already receives it in the normal broadcast, no extra
        // request needed.
        registered_teams(){
            return (this.$store.state.game.clients || [])
                .filter((cli) => cli.role == this.utils.USER_ROLES.PLAYER && cli.avatar_style && cli.avatar_style.color)
                .map((cli) => ({ username: cli.username, color: cli.avatar_style.color }));
        },
        taken_jersey_colors(){
            return this.registered_teams.map((t) => t.color);
        },
        // The number picker is gone - color is now the team's identifier.
        // Keep sending a number anyway (existing displays like Lobby.vue's
        // ring read user.avatar), derived automatically from the chosen
        // color's position in the palette instead of a separate manual pick.
        avatar(){
            const idx = this.jersey_color_options.findIndex((o) => o.hex === this.jersey_color);
            return idx >= 0 ? idx + 1 : 1;
        },
        // This event's own uploaded 3D model, or the app-wide default if it
        // hasn't configured one - see @/utils/jersey_model.js.
        jersey_model_url(){
            return get_active_jersey_model_url();
        }
    },
    watch: {
        needs_code_gate() { this.focus_name(); },
        avatar_menu() { this.focus_name(); },
        checking_username(value) { if (!value) this.focus_name(); },
        '$store.state.network'(value) { if (value === 'connected') this.focus_name(); },
        // Reply to CHECK_USERNAME (step 1's "Continuar") - a soft pre-check,
        // not a reservation, so client_auth() below still re-validates
        // authoritatively at the actual moment of registration (see
        // server.js's client_auth). Guarded on `.username` matching in case
        // a slow reply lands after the user has already edited the field.
        '$store.state.username_check_result' (result){
            if(!this.checking_username || result == null || result.username !== this.username)
                return;

            this.checking_username = false;

            if(result.available)
                this.avatar_menu = true;
            else
                window.toast.error('Ese nombre de equipo ya está en uso, elige otro.');
        },
        // Reply to CLIENT_AUTH (step 2's "Siguiente"). On success the actual
        // role/route transition still happens via the normal CLIENT_UPDATE
        // broadcast (handled elsewhere) - this only resolves the loading
        // state and, on failure, explains why and sends them back to fix it.
        '$store.state.client_auth_result' (result){
            if(!this.submitting_auth || result == null)
                return;

            this.submitting_auth = false;

            if(result.ok)
                return;

            window.toast.error(result.message || 'No se ha podido completar el registro, inténtalo de nuevo.');

            // The name was free during step 1's check but got taken by
            // someone else before this submit landed - back to step 1 so
            // they can pick a different one. A taken color, on the other
            // hand, is fixable without leaving step 2: taken_jersey_colors
            // already reacts to game.clients, so the picker just needs
            // another pick.
            if(result.reason === 'username_taken')
                this.avatar_menu = false;
        }
    },
    mounted(){
        this.focus_name();
        if(window.store.state.network == "connected"){
            window.network.wso.send(
                JSON.stringify(
                    {
                        action: window.RPC.UPDATE_VIEW, 
                        params: {
                            view: window.router.currentRoute._value.name, 
                            activity: (this.$store.state.game.question) ? this.$store.state.game.question.question.id : null
                        }
                    }
                )
            )
        }
        $('.modal-backdrop').remove();
    },
    methods : {
        focus_name() { this.$nextTick(() => { if (!this.needs_code_gate && !this.avatar_menu) this.$refs.teamName?.focus({ preventScroll: true }); }); },
        logout(){
            this.utils.reset_client();
        },
        client_auth()
        {
            if(this.username != null && this.username != '' && !this.submitting_auth)
            {
                this.submitting_auth = true;
                window.network.send(window.RPC.CLIENT_AUTH, {
                    username: this.username,
                    avatar: this.avatar,
                    avatar_style: { color: this.jersey_color },
                    device: window.device
                });
            }
        },
        toggle_avatar_menu()
        {
            if(this.username != null && this.username != '')
            {
                if(this.username.length < 6)
                {
                    window.toast.warning("El nombre de usuario tiene que tener como mínimo 6 caracteres.");
                    return;
                }

                if(this.username.length > 20)
                {
                    window.toast.warning("El nombre de usuario tiene un máximo de 20 caracteres.");
                    return;
                }

                if(this.checking_username)
                    return;

                this.checking_username = true;
                window.network.send(window.RPC.CHECK_USERNAME, { username: this.username });
            }
            else
                window.toast.warning("El nombre de usuario es obligatorio");
        },
        handle_username(evt)
        {
            if(evt.key == "Enter")
            {
                this.toggle_avatar_menu();
            }
        },
        return_username()
        {
            this.avatar_menu = false;
        }
    }
}
</script>

<style scoped>
.auth-picker {
    width: 100%;
    max-width: 700px;
    height: 80%;
    max-height: 640px;
    background: var(--vc-paper-50);
    border-radius: 0.75rem;
    overflow: hidden;
    box-shadow: 0 1rem 3rem rgba(0,0,0,0.15);
}

.auth-picker-header{
    background: var(--vc-ink-900);
    color: var(--vc-paper-50);
    padding: 1.5rem 2rem;
}

.auth-picker-step{
    font-family: "Archivo", sans-serif;
    font-weight: 700;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: rgba(255,255,255,0.55);
    margin-bottom: 0.4rem;
}

.username{
    text-transform: uppercase;
    font-family: "Archivo", sans-serif;
    font-size: 1.6rem;
    color: var(--vc-paper-50);
}

.auth-picker-body{
    padding: 2rem;
}

.auth-picker-title{
    font-family: "Archivo", sans-serif;
    font-size: 1.2rem;
    margin-bottom: 1.5rem;
}

.auth-picker-input{
    max-width: 360px;
    text-align: center;
}

.jersey-credits{
    font-size: 0.7rem;
    color: var(--vc-text-600, #999);
}
.jersey-credits summary{
    cursor: pointer;
    list-style: none;
}
.jersey-credits summary::-webkit-details-marker{
    display: none;
}
.jersey-credits a{
    color: inherit;
    text-decoration: underline;
}

.auth-picker-footer{
    padding: 1.5rem 2rem;
    border-top: 1px solid var(--vc-line);
}

.auth-logout{
    z-index: 30;
}
</style>